import React from "react";
import AnalysisPanel, { PanelEmpty } from "../AnalysisPanel";
import Pagination from "../../shared/Pagination";
import useClientPagination from "../../../hooks/useClientPagination";
import { getCiReportAvailabilityTrends } from "../../../api/competitiveIntelligence";
import { InferredOccupancy, categoryLabel } from "../../../utils/ciFormat";
import ThinScroll from "../../shared/ThinScroll";
import { PanelTakeaway, fmtRate, fmtPct } from "../shared";

const PAGE_SIZE = 15;

// Inline horizontal bar — used to give a quick visual sense of the percentage
// next to the numeric value. Width is clamped to [0, 100].
const PctBar = ({ value, color = "bg-[#32B1FF]" }) => {
  const clampedValue = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className="w-24 h-1.5 rounded bg-gray-100 dark:bg-gray-800 overflow-hidden">
      <div className={`h-full ${color}`} style={{ width: `${clampedValue}%` }} />
    </div>
  );
};

const AvailabilityTrendsBody = ({ rows }) => {
  if (!rows.length) {
    return <PanelEmpty message="No availability data tracked yet for this client." />;
  }
  return (
    <ThinScroll className="overflow-x-auto responsive-card-table-wrapper">
      <table className="w-full text-sm responsive-card-table">
        <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
            <th className="px-3 py-2.5 text-left font-semibold">Property</th>
            <th className="px-3 py-2.5 text-left font-semibold">Category</th>
            <th className="px-3 py-2.5 text-right font-semibold">Dates</th>
            <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">Limited Avail. %</th>
            <th className="px-3 py-2.5 text-left font-semibold">Min Stay %</th>
            <th className="px-3 py-2.5 text-left font-semibold">Bookable %</th>
            <th className="px-3 py-2.5 text-right font-semibold">Avg Occupancy</th>
            <th className="px-3 py-2.5 text-right font-semibold">Avg Rate</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={`${row.competitor_name}-${row.category_code}-${index}`}
              className={`border-b dark:border-gray-800 transition ${
                row.is_client
                  ? "bg-[#32B1FF]/5 dark:bg-[#32B1FF]/10 hover:bg-[#32B1FF]/10"
                  : "hover:bg-gray-50 dark:hover:bg-gray-800/50"
              }`}
            >
              <td data-label="Property" className={`px-3 py-2.5 whitespace-nowrap ${row.is_client ? "font-semibold text-[#32B1FF]" : "text-gray-900 dark:text-gray-100"}`}>
                {row.is_client ? `${row.competitor_name} (YOU)` : (row.competitor_name || "—")}
              </td>
              <td data-label="Category" className="px-3 py-2.5 text-gray-600 dark:text-gray-300 text-xs whitespace-nowrap">{categoryLabel(row.category_code)}</td>
              <td data-label="Dates" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{row.dates_tracked ?? 0}</td>
              <td data-label="Limited Avail. %" className="px-3 py-2.5 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <PctBar value={row.soldout_pct} color="bg-amber-500" />
                  <span className="text-gray-700 dark:text-gray-300 tabular-nums">{fmtPct(row.soldout_pct)}</span>
                </div>
              </td>
              <td data-label="Min Stay %" className="px-3 py-2.5 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <PctBar value={row.min_stay_pct} color="bg-amber-500" />
                  <span className="text-gray-700 dark:text-gray-300 tabular-nums">{fmtPct(row.min_stay_pct)}</span>
                </div>
              </td>
              <td data-label="Bookable %" className="px-3 py-2.5 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <PctBar value={row.bookable_pct} color="bg-emerald-500" />
                  <span className="text-gray-700 dark:text-gray-300 tabular-nums">{fmtPct(row.bookable_pct)}</span>
                </div>
              </td>
              <td data-label="Avg Occupancy" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap"><InferredOccupancy value={row.avg_occupancy} /></td>
              <td data-label="Avg Rate" className="px-3 py-2.5 text-right text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtRate(row.avg_rate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-4 py-2 text-[11px] text-gray-500 dark:text-gray-400">
        Limited-availability % is date-level — only counts dates where every site type is unavailable.
      </div>
    </ThinScroll>
  );
};

const AvailabilityTrendsBodyPaginated = ({ rows }) => {
  const { page, setPage, pageItems, totalPages, totalItems } = useClientPagination(rows, { pageSize: PAGE_SIZE });
  return (
    <>
      <AvailabilityTrendsBody rows={pageItems} />
      {totalPages > 1 && (
        <div className="px-5 pt-1 pb-3 border-t border-gray-200 dark:border-gray-800">
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalItems={totalItems}
            perPage={PAGE_SIZE}
            label="properties"
          />
        </div>
      )}
    </>
  );
};

const AvailabilityTrendsPanel = ({ clientId, say }) => (
  <AnalysisPanel
    id={`availability-trends-${clientId}`}
    title="Availability Trends"
    tip="For each competitor: % of dates at limited availability, % under min-stay restrictions, and % freely bookable, plus their avg occupancy and rate. Limited-availability % uses date-level logic — only counts dates where every site type is unavailable."
    description="Per-competitor limited-availability, min-stay, and bookable rates with average occupancy and price."
    fetcher={() => getCiReportAvailabilityTrends(clientId)}
    deps={[clientId]}
  >
    {({ data }) => {
      const rows = Array.isArray(data) ? data : [];
      // Client first, then by sold-out % desc (most-constrained competitors first).
      const sorted = [...rows].sort((rowA, rowB) => {
        if (!!rowB.is_client - !!rowA.is_client !== 0) return (!!rowB.is_client) - (!!rowA.is_client);
        return (rowB.soldout_pct ?? 0) - (rowA.soldout_pct ?? 0);
      });
      return (
        <>
          <PanelTakeaway panelKey="availability_trends" say={say} />
          <AvailabilityTrendsBodyPaginated rows={sorted} />
        </>
      );
    }}
  </AnalysisPanel>
);

export default AvailabilityTrendsPanel;
